import TopicChip from '@/components/topic-chip/topic-chip';
import { Card, Flex, Form, Input } from 'antd';
import styles from './document-details.module.scss';
import { useDocument } from '@/hooks/use-document/use-document';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { UpdateDocumentPayload } from '@/services/documents-service/documents-service.types';
import { Button } from '@/components/button/button';
import clsx from 'clsx';

export function DocumentDetails() {
  const { documentId = '' } = useParams();
  const { documentData, updatedDocument, isPendingDocumentUpdate } = useDocument(documentId);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!isEditing) return;
    form.setFieldsValue({
      name: documentData?.name || '',
      content: documentData?.content || '',
    });
  }, [documentData, form, isEditing]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const handleFinish = (values: UpdateDocumentPayload) => {
    updatedDocument(
      { name: values.name, content: values.content },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  return (
    <Flex vertical gap={16}>
      {!isEditing && (
        <Flex className={styles.editButtonWrapper} justify="end">
          <Button
            type="primary"
            className={styles.editButton}
            onClick={handleEdit}
            disabled={!!documentData?.user_id}
          >
            <span className={styles.editButtonText}>Edit</span>
          </Button>
        </Flex>
      )}

      <Card className={styles.detailsCard}>
        <div className={styles.chipWrapper}>
          <Flex align="center" gap={10}>
            <p className={styles.detailsLabel}>Company</p>
            <TopicChip title={documentData?.company_name || '---'} />
          </Flex>

          {documentData?.user_id && (
            <Flex align="center" gap={10}>
              <p className={styles.detailsLabel}>User ID</p>
              <TopicChip title={documentData.user_id} />
            </Flex>
          )}

          <Flex align="center" gap={10}>
            <p className={styles.detailsLabel}>Phone number</p>
            <TopicChip title={documentData?.phone || 'n/a'} />
          </Flex>

          <Flex align="center" gap={10}>
            <p className={styles.detailsLabel}>Last modified</p>
            <p>
              {documentData?.updated_at
                ? dayjs(documentData.updated_at).format('YYYY-MM-DD HH:mm:ss')
                : '---'}
            </p>
          </Flex>

          <Flex align="center" gap={10}>
            <p className={styles.detailsLabel}>Date Created</p>
            <p>
              {documentData?.created_at
                ? dayjs(documentData.created_at).format('YYYY-MM-DD HH:mm:ss')
                : '---'}
            </p>
          </Flex>
        </div>

        {isEditing ? (
          <Flex className={styles.formWrapper}>
            <Form
              className={styles.form}
              form={form}
              style={{ width: '100%' }}
              onFinish={handleFinish}
            >
              <Form.Item
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
                style={{ width: '100%', marginBottom: 0 }}
              >
                <Input className={styles.documentNameEditable} />
              </Form.Item>

              <Form.Item name="content">
                <Input.TextArea
                  className={styles.contentTextarea}
                  rows={10}
                  autoSize={{ minRows: 10, maxRows: 40 }}
                />
              </Form.Item>
            </Form>
          </Flex>
        ) : (
          <Flex vertical gap={4} className={styles.contentWrapper}>
            <p className={styles.documentName}>{documentData?.name}</p>
            <div style={{ whiteSpace: 'pre-wrap' }} className={styles.detailsWrapper}>
              {documentData?.content}
            </div>
          </Flex>
        )}
      </Card>

      {isEditing && (
        <Flex gap={8} className={styles.editSaveButtons}>
          <Button
            buttonVariant="secondary"
            onClick={handleCancel}
            className={clsx(styles.editButton, styles.cancelButton)}
          >
            Cancel
          </Button>
          <Button
            className={styles.editButton}
            buttonVariant="primary"
            onClick={() => form.submit()}
            loading={isPendingDocumentUpdate}
          >
            Save
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
